<template>
  <div class="modal fade" id="map-info-bar-settings-dialog" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content text-white rounded-4">
        <div class="modal-header border-0 px-4 pt-3">
          <h5 class="modal-title"><strong>Map information bar</strong></h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body p-0">

          <!-- ── Customise map information bar ── -->
          <div class="settings-section">
            <h6 class="section-title px-4 pt-1 mb-2">Customise map information bar</h6>

            <div class="info-bar-preview mx-4 mb-3">
              <div class="preview-markers">
                <span>Slot 1</span><span>Slot 2</span><span>Slot 3</span>
              </div>
              <div class="preview-pill">
                <span
                  v-for="(slot, i) in settings.slots"
                  :key="`preview-${i}`"
                  class="preview-slot"
                  :class="{ 'preview-slot--empty': slot === 'Empty' }"
                >{{ slot === 'Empty' ? '—' : slotLabelFor(slot) }}</span>
              </div>
            </div>

            <div
              v-for="(slot, i) in settings.slots"
              :key="`slot-row-${i}`"
              class="setting-row slot-row"
            >
              <span class="setting-label">Slot {{ i + 1 }}</span>
              <select
                class="form-select slot-select"
                :value="slot"
                @change="onSlotChange(i, $event)"
              >
                <optgroup v-for="grp in groupedSlotOptions" :key="grp.group" :label="grp.group">
                  <option v-for="opt in grp.options" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </optgroup>
              </select>
            </div>
          </div>

          <!-- ── Mini compass ── -->
          <div class="setting-row">
            <span class="setting-label">Show mini compass</span>
            <div class="form-check form-switch m-0">
              <input
                class="form-check-input setting-toggle"
                type="checkbox"
                role="switch"
                :checked="settings.showMiniCompass"
                @change="onToggle('showMiniCompass', $event)"
              />
            </div>
          </div>

          <!-- ── Map coordinate system ── -->
          <div class="settings-section">
            <h6 class="section-title px-4 pt-3 mb-2">Map coordinate system</h6>
            <div class="px-4 pb-1">
              <select
                class="form-select"
                :value="gridRefValue"
                @change="onGridRefChange"
              >
                <option v-for="opt in gridRefOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
              <div class="setting-note mt-2">Synced to your account and the HiiKER apps</div>
            </div>
          </div>

          <!-- ── Map crosshair ── -->
          <div class="settings-section">
            <h6 class="section-title px-4 pt-3 mb-2">Map crosshair</h6>

            <div class="setting-row">
              <span class="setting-label">Activate crosshairs</span>
              <div class="form-check form-switch m-0">
                <input
                  class="form-check-input setting-toggle"
                  type="checkbox"
                  role="switch"
                  :checked="settings.crosshairEnabled"
                  @change="onToggle('crosshairEnabled', $event)"
                />
              </div>
            </div>

            <template v-if="settings.crosshairEnabled">
              <div class="px-4 pt-3">
                <div class="subsection-label mb-2">Choose crosshair</div>
                <div class="style-grid">
                  <button
                    v-for="n in styleIndexes"
                    :key="`crosshair-style-${n}`"
                    type="button"
                    class="style-swatch"
                    :class="{ 'style-swatch--selected': settings.crosshairStyle === n }"
                    :aria-label="`Crosshair style ${n}`"
                    @click="onCrosshairStyleSelect(n)"
                  >
                    <MapCrosshair :variant="n" :color="currentColorHex" preview />
                  </button>
                </div>
              </div>

              <div class="px-4 pt-3 pb-1">
                <div class="subsection-label mb-2">Choose color</div>
                <div class="color-grid">
                  <button
                    v-for="(color, idx) in crosshairColorsList"
                    :key="`crosshair-color-${idx}`"
                    type="button"
                    class="color-swatch"
                    :class="{ 'color-swatch--selected': settings.crosshairColor === idx }"
                    :style="{ backgroundColor: color.hex }"
                    :aria-label="color.name"
                    @click="onCrosshairColorSelect(idx)"
                  ></button>
                </div>
              </div>
            </template>
          </div>

          <!-- ── Customise current location icon ── -->
          <div class="settings-section">
            <h6 class="section-title px-4 pt-3 mb-2">Customise current location icon</h6>

            <div class="px-4">
              <div class="style-grid">
                <button type="button" class="style-swatch" :class="{ 'style-swatch--selected': settings.locationIconStyle === 1 }" aria-label="Location icon style 1" @click="onLocationIconStyleSelect(1)">
                  <svg viewBox="0 0 24 24" width="28" height="28">
                    <circle cx="12" cy="12" r="10" fill="#3C83F7" fill-opacity="0.25" />
                    <path d="M12 5 L16 15 L12 12.5 L8 15 Z" fill="#3C83F7" />
                  </svg>
                </button>
                <button type="button" class="style-swatch" :class="{ 'style-swatch--selected': settings.locationIconStyle === 2 }" aria-label="Location icon style 2" @click="onLocationIconStyleSelect(2)">
                  <svg viewBox="0 0 24 24" width="28" height="28">
                    <circle cx="12" cy="12" r="10" fill="#DB2B35" fill-opacity="0.25" />
                    <path d="M12 5 L16 15 L12 12.5 L8 15 Z" fill="#DB2B35" />
                  </svg>
                </button>
                <button type="button" class="style-swatch" :class="{ 'style-swatch--selected': settings.locationIconStyle === 3 }" aria-label="Location icon style 3" @click="onLocationIconStyleSelect(3)">
                  <svg viewBox="0 0 24 24" width="28" height="28">
                    <circle cx="12" cy="12" r="10" fill="#FDDF00" fill-opacity="0.25" />
                    <path d="M12 5 L16 15 L12 12.5 L8 15 Z" fill="#FDDF00" />
                  </svg>
                </button>
                <button type="button" class="style-swatch" :class="{ 'style-swatch--selected': settings.locationIconStyle === 4 }" aria-label="Location icon style 4" @click="onLocationIconStyleSelect(4)">
                  <svg viewBox="0 0 24 24" width="28" height="28">
                    <path d="M12 3 L15 10 L12 8.2 L9 10 Z" fill="#3C83F7" />
                    <circle cx="12" cy="17" r="4" fill="#3C83F7" />
                  </svg>
                </button>
                <button type="button" class="style-swatch" :class="{ 'style-swatch--selected': settings.locationIconStyle === 5 }" aria-label="Location icon style 5" @click="onLocationIconStyleSelect(5)">
                  <svg viewBox="0 0 24 24" width="28" height="28">
                    <path d="M12 2 L19 17 L5 17 Z" fill="#3C83F7" fill-opacity="0.25" />
                    <circle cx="12" cy="17" r="4" fill="#3C83F7" />
                  </svg>
                </button>
                <button type="button" class="style-swatch" :class="{ 'style-swatch--selected': settings.locationIconStyle === 6 }" aria-label="Location icon style 6" @click="onLocationIconStyleSelect(6)">
                  <svg viewBox="0 0 24 24" width="28" height="28">
                    <circle cx="12" cy="12" r="10" fill="#3C83F7" />
                    <path d="M12 6 L16 14 L12 12 L8 14 Z" fill="#fff" />
                  </svg>
                </button>
              </div>
            </div>

            <div class="px-4 pt-3">
              <div class="subsection-label mb-1">Choose icon size</div>
              <div class="icon-size-list">
                <div
                  v-for="size in iconSizeOptions"
                  :key="size"
                  class="icon-size-row"
                  @click="onIconSizeSelect(size)"
                >
                  <span>{{ iconSizeLabel(size) }}</span>
                  <span v-if="settings.locationIconSize === size" class="material-icons check-icon">check</span>
                </div>
              </div>
            </div>
          </div>

          <!-- ── Hide current location marker ── -->
          <div class="setting-row">
            <span class="setting-label">Hide current location marker</span>
            <div class="form-check form-switch m-0">
              <input
                class="form-check-input setting-toggle"
                type="checkbox"
                role="switch"
                :checked="settings.hideLocationMarker"
                @change="onToggle('hideLocationMarker', $event)"
              />
            </div>
          </div>

          <!-- ── Lock map ── -->
          <div class="setting-row">
            <div class="flex-grow-1 pe-3">
              <span class="setting-label">Lock map (disable pan and zoom)</span>
              <div class="setting-sub">Turns off until you reload the page</div>
            </div>
            <div class="form-check form-switch m-0">
              <input
                class="form-check-input setting-toggle"
                type="checkbox"
                role="switch"
                :checked="interactionsLocked"
                @change="onInteractionsLockedChange"
              />
            </div>
          </div>

          <button class="reset-btn mt-4 mb-4" @click="resetToDefault">RESET TO DEFAULT</button>

        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import emitter from '../../../utils/bus';
import {
  MapInfoBarSettings,
  InfoBarSlot,
  IconSize,
  StyleIndex,
  ColorIndex,
  groupedInfoBarSlotOptions,
  InfoBarSlotGroup,
  ICON_SIZE_OPTIONS,
  STYLE_INDEXES,
  CROSSHAIR_COLORS,
  DEFAULT_MAP_INFO_BAR_SETTINGS,
  getMapInfoBarSettings,
  saveMapInfoBarSettings,
  slotLabel,
} from '../../../utils/mapInfoBarSettings';
import { GridRef, GridRefUtils } from '../../../utils/helpers/GridRefPreference';
// NOTE: MapCrosshair.vue is being built in a parallel branch (props: style: Number,
// color: String, preview: Boolean). This dialog won't bundle until it lands.
import MapCrosshair from './MapCrosshair.vue';

/** Fixed display order for the coordinate-system select. */
const GRID_REF_MEMBERS: GridRef[] = [
  GridRef.GbGrid,
  GridRef.IeGrid,
  GridRef.NzGrid,
  GridRef.LatLon,
  GridRef.LatLonDms,
];


export default defineComponent({
  name: 'MapInfoBarSettingsDialog',

  components: { MapCrosshair },

  props: {
    interactionsLocked: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:interactionsLocked'],

  data() {
    return {
      settings: getMapInfoBarSettings() as MapInfoBarSettings,
      gridRefValue: GridRefUtils.preference().gridRef as GridRef,
    };
  },

  computed: {
    groupedSlotOptions(): InfoBarSlotGroup[] {
      return groupedInfoBarSlotOptions();
    },

    gridRefOptions(): Array<{ value: GridRef; label: string }> {
      return GRID_REF_MEMBERS.map((g) => ({
        value: g,
        label: `${new GridRefUtils(g).toPrettyString()}`,
      }));
    },

    currentColorHex(): string {
      return CROSSHAIR_COLORS[this.settings.crosshairColor].hex;
    },

    styleIndexes(): ReadonlyArray<StyleIndex> {
      return STYLE_INDEXES;
    },

    crosshairColorsList(): ReadonlyArray<{ name: string; hex: string }> {
      return CROSSHAIR_COLORS;
    },

    iconSizeOptions(): ReadonlyArray<IconSize> {
      return ICON_SIZE_OPTIONS;
    },
  },

  mounted() {
    this.settings = getMapInfoBarSettings();
    this.gridRefValue = GridRefUtils.preference().gridRef;
    this.$el.addEventListener('shown.bs.modal', this.onShown);
  },

  beforeUnmount() {
    this.$el.removeEventListener('shown.bs.modal', this.onShown);
  },

  methods: {
    onShown() {
      // Settings (and the synced coordinate preference) may have changed elsewhere
      // since this dialog was last opened — reload every time it's shown.
      this.settings = getMapInfoBarSettings();
      this.gridRefValue = GridRefUtils.preference().gridRef;
    },

    persist() {
      saveMapInfoBarSettings(this.settings);
      emitter.emit('map-info-bar-settings:changed', { ...this.settings, slots: [...this.settings.slots] });
    },

    slotLabelFor(slot: InfoBarSlot): string {
      return slotLabel(slot);
    },

    iconSizeLabel(size: IconSize): string {
      return size === 'Small' ? 'Small (Default)' : size;
    },

    onSlotChange(index: number, e: Event) {
      const value = (e.target as HTMLSelectElement).value as InfoBarSlot;
      this.settings.slots[index] = value;
      this.persist();
    },

    onToggle(key: 'showMiniCompass' | 'crosshairEnabled' | 'hideLocationMarker', e: Event) {
      this.settings[key] = (e.target as HTMLInputElement).checked;
      this.persist();
    },

    onGridRefChange(e: Event) {
      const value = Number((e.target as HTMLSelectElement).value) as GridRef;
      this.gridRefValue = value;
      // Persists/syncs itself (account + apps) — nothing else to save here.
      GridRefUtils.setPreference(value).catch(() => {});
    },

    onCrosshairStyleSelect(style: number) {
      this.settings.crosshairStyle = style as StyleIndex;
      this.persist();
    },

    onCrosshairColorSelect(idx: number) {
      this.settings.crosshairColor = idx as ColorIndex;
      this.persist();
    },

    onLocationIconStyleSelect(style: number) {
      this.settings.locationIconStyle = style as StyleIndex;
      this.persist();
    },

    onIconSizeSelect(size: IconSize) {
      this.settings.locationIconSize = size;
      this.persist();
    },

    onInteractionsLockedChange(e: Event) {
      this.$emit('update:interactionsLocked', (e.target as HTMLInputElement).checked);
    },

    resetToDefault() {
      this.settings = {
        ...DEFAULT_MAP_INFO_BAR_SETTINGS,
        slots: [...DEFAULT_MAP_INFO_BAR_SETTINGS.slots] as MapInfoBarSettings['slots'],
      };
      this.persist();
      // Coordinate system is intentionally left untouched by reset.
      this.$emit('update:interactionsLocked', false);
    },
  },
});
</script>

<style scoped>
/* Shorter than Bootstrap's full-viewport scrollable dialog; the body scrolls inside. */
.modal-dialog-scrollable {
  max-height: min(70vh, 640px);
}
.modal-dialog-scrollable .modal-content {
  max-height: min(70vh, 640px);
}

.modal-content {
  background: #111;
}

.settings-section {
  border-bottom: 1px solid #2e2e2e;
  padding-bottom: 4px;
}

.section-title {
  font-size: 0.9rem;
  color: #ccc;
}

.subsection-label {
  font-size: 0.82rem;
  color: #999;
  padding: 0 0;
}

.setting-note {
  font-size: 0.78rem;
  color: #999;
}

.setting-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #2e2e2e;
}

.slot-row {
  flex-wrap: nowrap;
}

.setting-label {
  font-size: 0.95rem;
}

.setting-sub {
  margin-top: 2px;
  font-size: 0.78rem;
  line-height: 1.3;
  color: #999;
}

.setting-toggle {
  width: 3em !important;
  height: 1.5em !important;
  background-color: #444 !important;
  border-color: #555 !important;
  cursor: pointer;
  flex-shrink: 0;
}

.setting-toggle:checked {
  background-color: #fddf00 !important;
  border-color: #fddf00 !important;
}

/* ── Form select (dark) ───────────────────────────────────────────────── */
.form-select {
  background-color: #2d2d2d;
  color: #fff;
  border: 1px solid #444;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e");
}

.form-select:focus {
  background-color: #2d2d2d;
  color: #fff;
  border-color: #fddf00;
  box-shadow: none;
}

.form-select option,
.form-select optgroup {
  background-color: #2d2d2d;
  color: #fff;
}

.slot-select {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 65%;
}

/* ── Info bar preview ─────────────────────────────────────────────────── */
.info-bar-preview {
  min-width: 0;
}

.preview-markers {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  text-align: center;
  margin-bottom: 4px;
}

.preview-markers span {
  color: #fddf00;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.preview-pill {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 999px;
  overflow: hidden;
}

.preview-slot {
  min-width: 0;
  padding: 8px 6px;
  text-align: center;
  font-size: 0.8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-right: 1px solid #333;
}

.preview-slot:last-child {
  border-right: none;
}

.preview-slot--empty {
  color: #666;
}

/* ── Style / color grids ──────────────────────────────────────────────── */
.style-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.style-swatch {
  min-width: 0;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e8e2d4;
  border: 3px solid transparent;
  border-radius: 12px;
  padding: 6px;
  cursor: pointer;
  transition: border-color 0.15s;
}

.style-swatch--selected {
  border-color: #fddf00;
}

.color-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
}

.color-swatch {
  min-width: 0;
  aspect-ratio: 1;
  border: none;
  border-radius: 10px;
  outline: 3px solid transparent;
  outline-offset: 2px;
  cursor: pointer;
  transition: outline-color 0.15s;
}

.color-swatch--selected {
  outline-color: #fddf00;
}

/* ── Icon size list ───────────────────────────────────────────────────── */
.icon-size-list {
  border-top: 1px solid #2e2e2e;
}

.icon-size-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #2e2e2e;
  cursor: pointer;
  font-size: 0.9rem;
}

.icon-size-row:hover {
  opacity: 0.85;
}

.check-icon {
  color: #fddf00;
  font-size: 20px;
}

/* ── Reset button ─────────────────────────────────────────────────────── */
.reset-btn {
  display: block;
  width: calc(100% - 40px);
  margin: 0 20px;
  padding: 12px;
  background: #2d2d2d;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}

.reset-btn:hover {
  background: #3a3a3a;
}
</style>
